import { useMemo } from "react";
import useDiagnostics from "admin/hooks/useDiagnostics";
import type { DiagnosticListItem, DiagnosticSeverity } from "admin/types/interfaces/diagnostics";
import { turnAnchorId } from "~/pages/manifold-nextgen/components/conversations/useAnchoredScroll";

/** Severities worth navigating to, driving Prev/Next flagged and the header dot. */
const FLAGGED_SEVERITIES: readonly DiagnosticSeverity[] = ["high", "medium"];

export interface ConversationDiagnostics {
  /**
   * Diagnostics keyed by root trace id, which is the anchor the transcript's
   * turns expose, in chronological order. Items whose turn id yields no anchor
   * are dropped, so this is also the set of turns the layer can mark.
   */
  byTurnId: Map<string, DiagnosticListItem>;
  /** Mean `averageScore` across the joined turns that have one; `null` when none do. */
  sessionScore: number | null;
  /** Joined turns per severity, so the band never describes a turn the transcript lacks. */
  severityCounts: Record<DiagnosticSeverity, number>;
  /** Anchor ids of the high/medium turns, chronological. */
  flaggedTurnIds: string[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Every diagnostic for one session in a single unpaginated request, plus the
 * session-level rollups the in-session diagnostics layer renders.
 */
export const useConversationDiagnostics = (conversationId: string): ConversationDiagnostics => {
  const { items, isLoading, error } = useDiagnostics({
    conversationId,
    limit: null,
    sortKey: "created_at",
    sortOrder: "asc",
  });

  const rollups = useMemo(() => {
    const byTurnId = new Map<string, DiagnosticListItem>();
    for (const item of items) {
      const anchorId = turnAnchorId(item.turnId);
      if (anchorId) byTurnId.set(anchorId, item);
    }

    // Every rollup reads off the map, not `items`, so one turn is counted once
    // however many diagnostics it accumulated and the totals only ever cover
    // turns the transcript can actually mark.
    const severityCounts: Record<DiagnosticSeverity, number> = { high: 0, medium: 0, low: 0 };
    const flaggedTurnIds: string[] = [];
    let scoreTotal = 0;
    let scoredCount = 0;

    for (const [anchorId, item] of byTurnId) {
      severityCounts[item.severity] += 1;
      if (FLAGGED_SEVERITIES.includes(item.severity)) flaggedTurnIds.push(anchorId);
      if (item.averageScore != null) {
        scoreTotal += item.averageScore;
        scoredCount += 1;
      }
    }

    return {
      byTurnId,
      severityCounts,
      flaggedTurnIds,
      sessionScore: scoredCount > 0 ? scoreTotal / scoredCount : null,
    };
  }, [items]);

  return { ...rollups, isLoading, error };
};
